import type { ChangeEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import debounce from "lodash/debounce";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { isValidURL } from "@/react/helpers/urlHelpers/isValidUrl";
import type { ListItem as ListItemProp } from "@circle-react-shared/BuilderUI/DefaultBlocks/Content/UlListBlock/UlBlock";
import { Form } from "@circle-react-uikit/Form";
import { ListFieldItemActions } from "./ListFieldItemActions";
import { ListFieldItemHeader } from "./ListFieldItemHeader";
import { ListFieldItemPrefix } from "./ListFieldItemPrefix";
import { ListFieldItemURLActions } from "./ListFieldItemURLActions";

export interface ListFieldItemProps {
  index: number;
  isDeleteDisabled: boolean;
  item: ListItemProp;
  name: string;
  onClickDeleteItemHandler: () => void;
  onClickDuplicateItemHandler: () => void;
}

export const ListFieldItem = ({
  name,
  item,
  index,
  isDeleteDisabled = false,
  onClickDeleteItemHandler,
  onClickDuplicateItemHandler,
}: ListFieldItemProps) => {
  const itemProps = {
    hideDescription: true,
    hideBorder: true,
    className: "!p-0 !flex flex-col gap-2 w-full",
    hideLabel: true,
  };

  const hasURL = Boolean(item.url);
  const [hasLink, setHasLink] = useState(hasURL);
  const { setError, setValue } = useFormContext();

  const debouncedValidateUrl = useMemo(
    () =>
      debounce((url: string, name: string, index: number) => {
        if (!isValidURL(url)) {
          setError(`${name}.${index}.url`, {
            type: "manual",
            message: t("builder_ui.prop_type.list.errors.invalid_url"),
          });
        }
      }, 1000),
    [setError],
  );

  const onClickToggleLinkHandler = () => {
    setHasLink(prev => !prev);
    setValue(`${name}.${index}.url`, "");
  };

  useEffect(() => {
    if (!hasURL) {
      return;
    }
    setHasLink(true);
  }, [hasURL]);

  return (
    <div key={item.id} className="flex flex-col items-center gap-3">
      <Form.Item name={`${name}.${index}.value`} {...itemProps}>
        <ListFieldItemHeader title={t("builder_ui.prop_type.list.item")}>
          <ListFieldItemActions
            onClickDelete={onClickDeleteItemHandler}
            onClickDuplicate={onClickDuplicateItemHandler}
            onClickAddLink={onClickToggleLinkHandler}
            isDeleteDisabled={isDeleteDisabled}
          />
        </ListFieldItemHeader>
        <Form.Input
          name={`${name}.${index}.value`}
          prefix={<ListFieldItemPrefix name={`${name}.${index}.bullet`} />}
        />
      </Form.Item>
      {hasLink && (
        <Form.Item
          name={`${name}.${index}.url`}
          placeholder={t("builder_ui.prop_type.list.add_link_placeholder")}
          errorClassName="text-red-500 text-sm"
          {...itemProps}
        >
          <ListFieldItemHeader title={t("builder_ui.prop_type.list.link")}>
            <ListFieldItemURLActions onClickDelete={onClickToggleLinkHandler} />
          </ListFieldItemHeader>
          <Form.Input
            name={`${name}.${index}.url`}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              debouncedValidateUrl(event.target.value, name, index);
            }}
          />
        </Form.Item>
      )}
    </div>
  );
};
